// Event-based toast system for non-React contexts (e.g., axios interceptors)
// This allows showing toasts from anywhere without React context

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';

export interface ToastEventPayload {
  type: ToastType;
  message: string;
  duration?: number;
}

const TOAST_EVENT_NAME = 'app:toast';

// Event listener for toasts - used by ToastProvider
type ToastEventListener = (event: CustomEvent<ToastEventPayload>) => void;

export const toastEvents = {
  // Show a toast by dispatching an event
  show: (payload: ToastEventPayload): void => {
    const event = new CustomEvent<ToastEventPayload>(TOAST_EVENT_NAME, {
      detail: payload,
    });
    window.dispatchEvent(event);
  },

  // Subscribe to toast events
  subscribe: (listener: ToastEventListener): (() => void) => {
    window.addEventListener(TOAST_EVENT_NAME, listener as EventListener);
    return () => {
      window.removeEventListener(TOAST_EVENT_NAME, listener as EventListener);
    };
  },

  // Convenience methods
  success: (message: string, duration?: number) =>
    toastEvents.show({ type: 'success', message, duration }),
  error: (message: string, duration?: number) =>
    toastEvents.show({ type: 'error', message, duration }),
  warning: (message: string, duration?: number) =>
    toastEvents.show({ type: 'warning', message, duration }),
  info: (message: string, duration?: number) =>
    toastEvents.show({ type: 'info', message, duration }),
  loading: (message: string) =>
    toastEvents.show({ type: 'loading', message, duration: 0 }),
};

export default toastEvents;
